import * as Sentry from "@sentry/browser";
import { APP_VERSION } from "./default-category.service";
import { getCookie } from "../utils/attribution.util";
/**
 * A singleton that inits sentry and GA4 dataLayer events
 */
declare global {
  interface Window {
    dataLayer: any[];
  }
}
class TrackingService {
  public static instance: TrackingService;
  private initialized: boolean = false;

  constructor() {
    if (TrackingService.instance) {
      return TrackingService.instance;
    }

    TrackingService.instance = this;

    if (!window.dataLayer) {
      window.dataLayer = [];
    }

    // Attach the service to dataLayer
    (window.dataLayer as any).trackingService = this;
  }

  public init(): void {
    if (this.initialized) {
      return;
    }
    try {
      Sentry.init({
        dsn: process.env.SENTRY_DSN,
        environment: "production",
        tracesSampleRate: 0.2,
        replaysSessionSampleRate: 0.2,
        replaysOnErrorSampleRate: 1.0,
        beforeSend: (event) => {
          if (event.level !== "error") {
            return event;
          }
          const additional = event.contexts?.additional;
          // app_version because I want to get only the exceptions that I catch
          if (additional && additional.app_version) {
            return event;
          }

          return null;
        },
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
          }),
          Sentry.feedbackIntegration(),
        ],
      });
      this.initialized = true;
      this.setTag("app_version", APP_VERSION);
    } catch (error) {
      console.error("Failed to initialize Sentry:", error);
    }
  }

  public captureException(
    error: Error,
    context?: Record<string, any>
  ): string | undefined {
    if (!this.initialized) {
      console.warn("Sentry not initialized. Call init() first");
      return undefined;
    }

    try {
      return Sentry.withScope((scope) => {
        if (context) {
          scope.setContext("additional", {
            ...context,
            app_version: APP_VERSION,
          });
        }
        return Sentry.captureException(error);
      });
    } catch (e) {
      console.error("Failed to capture exception:", e);
      return undefined;
    }
  }

  public captureMessage(
    message: string,
    level?: Sentry.SeverityLevel
  ): string | undefined {
    if (!this.initialized) {
      console.warn("Sentry not initialized. Call init() first");
      return undefined;
    }

    try {
      return Sentry.captureMessage(message, level);
    } catch (e) {
      console.error("Failed to capture message:", e);
      return undefined;
    }
  }

  public pushEvent(eventName: string, params: Record<string, any> = {}): void {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: eventName,
        ...this.getCommonEventParams(),
        ...params,
      });
    } catch (error) {
      console.error("Failed to push GA4 event:", error);
    }
  }

  private getCommonEventParams(): Record<string, string> {
    const utmData = this.getUtmFromCookies();
    const visitorId = getCookie("ryb_visitor_id");
    return {
      funnel_type: this.detectFunnelType(),
      page_url: window.location.href,
      ...(visitorId ? { visitor_id: visitorId } : {}),
      ...utmData,
    };
  }

  private detectFunnelType(): string {
    const url = window.location.href.toLowerCase();
    const params = new URLSearchParams(window.location.search);
    if (params.has("step") || url.includes("wizard")) return "wizard";
    if (url.includes("finder") || url.includes("calendar")) return "finder";
    if (url.includes("event") || url.includes("camp") || url.includes("clinic"))
      return "events";
    return "unknown";
  }

  private getUtmFromCookies(): Record<string, string> {
    const cookieValue = getCookie("campaignCookies");
    if (!cookieValue) return {};
    try {
      const decoded = atob(cookieValue);
      const params = new URLSearchParams(
        decoded.startsWith("?") ? decoded.slice(1) : decoded
      );
      const result: Record<string, string> = {};
      ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach((key) => {
        const val = params.get(key);
        if (val) result[key] = val;
      });
      return result;
    } catch {
      return {};
    }
  }

  public setTag(key: string, value: string): void {
    if (!this.initialized) {
      console.warn("Sentry not initialized. Call init() first");
      return;
    }

    try {
      Sentry.setTag(key, value);
    } catch (error) {
      console.error("Failed to set tag:", error);
    }
  }
}

export const trackingService = new TrackingService();
